import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';

export default function SeoContent() {
  return (
    <>
      <section id="about" className="relative w-full py-16 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-primary-500 text-lg"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              What Is <span className="text-primary-500">Sugargoo Spreadsheet</span>?
            </h2>
          </div>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-4">
            <p>
              A <strong>Sugargoo Spreadsheet</strong> is a carefully curated and regularly updated database that catalogues thousands of products available through the Sugargoo shopping agent platform. It serves as a comprehensive product discovery tool for international shoppers who want to access Chinese e-commerce marketplaces like Taobao, Weidian, and 1688 without the language barrier or navigation complexity.
            </p>
            <p>
              These spreadsheets have become essential resources within the global replica and streetwear fashion communities. Rather than spending hours manually searching through Chinese marketplace listings — often with confusing translations and unreliable image searches — users can browse organized collections where every product has already been verified for quality, accurate sizing, and reliable sellers. Each entry typically includes direct purchase links, QC (Quality Control) reference photos, price comparisons in multiple currencies, sizing recommendations, and community-vetted seller ratings.
            </p>
            <p>
              For beginners navigating international shopping agents for the first time, a well-maintained Sugargoo Spreadsheet eliminates the overwhelming guesswork. New users can simply browse by category, find products that match their style preferences, and click through to make purchases with confidence. The time savings are dramatic — what might take a novice several days of trial and error can be accomplished in minutes with a properly organized spreadsheet. The Sugargoo Spreadsheet ecosystem has grown into a vital knowledge-sharing network where experienced buyers contribute their finds for the benefit of the entire community.
            </p>
          </div>

          <div className="mt-8 p-5 md:p-6 bg-background-100 rounded-xl border border-background-200/70">
            <h3 className="font-heading font-semibold text-foreground-900 mb-3 text-base md:text-lg">
              Key Benefits of Using Sugargoo Spreadsheet
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: 'ri-time-line', title: 'Save Hours of Searching', desc: 'Skip manual browsing — find curated products instantly' },
                { icon: 'ri-shield-check-line', title: 'Verified Quality', desc: 'Every product reviewed by experienced community members' },
                { icon: 'ri-translate-2', title: 'No Language Barrier', desc: 'All listings translated and organized in English' },
                { icon: 'ri-price-tag-3-line', title: 'Price Comparison', desc: 'Compare prices across multiple sellers at a glance' },
              ].map((item) => (
                <div key={item.title} className="flex gap-3 p-3">
                  <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-secondary-600 text-base`}></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">{item.title}</h4>
                    <p className="text-xs text-foreground-600 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative w-full py-16 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-3">
            Why Thousands Search for <span className="text-primary-500">Sugargoo Spreadsheet</span>
          </h2>
          <p className="text-foreground-600 text-sm text-center mb-10 max-w-xl mx-auto">
            Traditional searching versus curated spreadsheet browsing — see the difference
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-background-300">
                  <th className="text-left py-4 px-4 text-foreground-500 font-medium text-xs uppercase tracking-wider">Aspect</th>
                  <th className="text-left py-4 px-4 text-foreground-400 font-medium text-xs uppercase tracking-wider">Traditional Searching</th>
                  <th className="text-left py-4 px-4 text-primary-600 font-medium text-xs uppercase tracking-wider bg-primary-50/50 rounded-tr-lg">Sugargoo Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { aspect: 'Time Required', trad: 'Hours to days per product', spread: 'Minutes to browse hundreds' },
                  { aspect: 'Product Discovery', trad: 'Limited by search terms', spread: 'Curated categories & trends' },
                  { aspect: 'Organization', trad: 'Scattered bookmarks & tabs', spread: 'Structured database format' },
                  { aspect: 'Beginner Difficulty', trad: 'Steep learning curve', spread: 'Intuitive & beginner-friendly' },
                  { aspect: 'Research Efficiency', trad: 'Low — manual verification', spread: 'High — community-vetted data' },
                ].map((row, i) => (
                  <tr key={row.aspect} className={`border-b border-background-200/70 ${i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}`}>
                    <td className="py-3.5 px-4 font-medium text-foreground-800">{row.aspect}</td>
                    <td className="py-3.5 px-4 text-foreground-500">{row.trad}</td>
                    <td className="py-3.5 px-4 text-foreground-800 font-medium">{row.spread}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 text-center">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Browse Sugargoo Spreadsheet Now
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      <section className="relative w-full py-16 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-star-line text-accent-500 text-lg"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              Is Sugargoo Legit? <span className="text-accent-500">A Trust Overview</span>
            </h2>
          </div>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-4">
            <p>
              One of the most common questions among international shoppers discovering Chinese marketplaces is: <strong>is Sugargoo legit?</strong> The answer is a confident yes — Sugargoo is a well-established shopping agent platform that has been operating since 2019, serving hundreds of thousands of customers worldwide. The platform acts as an intermediary between international buyers and Chinese e-commerce sellers, handling everything from purchase execution to quality inspection photography, warehouse consolidation, and international shipping.
            </p>
            <p>
              Sugargoo maintains transparent business practices with clearly published fee structures, shipping rate tables, and service terms. The platform offers multiple secure payment methods including PayPal, which provides buyer protection for added peace of mind. Their warehouse team photographs every item upon arrival — these QC (Quality Control) photos allow buyers to verify product condition before international shipping, and items that do not match descriptions can be returned or exchanged at this stage. This inspection layer is a critical safety feature that protects buyers from receiving incorrect or defective products.
            </p>
            <p>
              However, it is important to understand that Sugargoo is a service platform, not the seller of the goods themselves. The quality and authenticity of individual products depend on the original sellers on platforms like Taobao and Weidian. This is precisely why a high-quality Sugargoo Spreadsheet is so valuable — it directs users toward products and sellers that have already been vetted by experienced community members, significantly reducing the risk of disappointing purchases. When used responsibly alongside trusted spreadsheets and community resources, Sugargoo provides a reliable gateway to products that would otherwise be inaccessible to international buyers.
            </p>
          </div>
        </div>
      </section>

      <section id="guides" className="relative w-full py-16 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
            Sugargoo Spreadsheet <span className="text-primary-500">Complete Guide</span>
          </h2>

          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8 mb-6">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
              Featured Article
            </span>
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              Sugargoo Spreadsheet Complete Guide 2026
            </h3>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5">
              Our comprehensive guide covers everything you need to know about Sugargoo Spreadsheet — from getting started as a complete beginner 
              to mastering advanced product research techniques. Learn how to find the best deals, identify trusted sellers, understand sizing charts, 
              calculate shipping costs, and maximize your savings with the latest coupon codes and discount strategies.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/article/sugargoo-spreadsheet-complete-guide"
                className="inline-flex items-center px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Read Full Guide
                <i className="ri-arrow-right-line ml-1.5"></i>
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center px-5 py-2.5 rounded-full border border-background-300 text-foreground-700 text-sm font-semibold hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-shopping-cart-line ml-1.5"></i>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}